/* The plain words this console uses for a recorded interruption: where it was
 * placed, how sure that is, what it means and what to do next. The panel, the
 * path drawing and the timeline all read from here so they cannot drift apart.
 * The appliance's own, stricter wording stays available beside the measurements. */

// `at` is the part of the path drawing a place lights up. `network` marks the
// places the network team owns: an internet line, or the link to the cloud.
export const PLACES = {
  application: {
    label: "The device or its app", at: "device", network: false,
    where: "On the device side",
    meaning: "This one connection went quiet while the same device kept using the network for other things. The app may have paused or ended, or something on the device or its Wi-Fi held this connection back.",
    next: "Start with the device: did the call end, did the app freeze, did the laptop sleep? If calls keep freezing on Wi-Fi while everything else works, check the access point's media prioritisation (WMM) settings.",
  },
  lan_or_device: {
    label: "The device or office network", at: "lan", network: false,
    where: "Between the device and this box",
    meaning: "The device went silent on every connection at once. We can't tell the device from the office network it sits on.",
    next: "Check whether the device slept, dropped off Wi-Fi or lost its cable at this time.",
  },
  access_circuit: {
    label: "An internet line", at: "uplink", network: true,
    where: "On an internet line",
    meaning: "The internet line carrying this connection was losing test traffic at the same moment. That probably caused the freeze. It is a strong hint, not proof.",
    next: "If this repeats on the same line, send this report to that line's provider.",
  },
  tunnel: {
    label: "The Weavian link", at: "uplink", network: true,
    where: "Between this box and the Weavian cloud",
    meaning: "Traffic went missing on the stretch between this box and the Weavian cloud. Both ends counted the same seconds, so this is measured, not guessed.",
    next: "This is on the network side, not the device and not the service. Send this report to Weavian support.",
  },
  beyond_anchor: {
    label: "The public internet", at: "internet", network: false,
    where: "On the public internet, past the Weavian cloud",
    meaning: "The Weavian cloud could not reach the service at the time.",
    next: "Check whether the service was reachable from elsewhere at this time.",
  },
  past_anchor: {
    label: "Beyond the Weavian cloud", at: "internet", network: false,
    where: "Beyond the Weavian cloud",
    meaning: "Everything this box sent arrived at the Weavian cloud, and nothing came back to it from the other side. The break is past that point: the public internet or the service itself. We can't tell which of the two.",
    next: "Check whether the service had a problem at this time: its status page, or the other people on the call. If this repeats across different services, send this report to Weavian support.",
  },
  far_end: {
    label: "The service itself", at: "far", network: false,
    where: "At the service",
    meaning: "The service stopped answering this connection.",
    next: "Check the service's status page, or ask the other side what they saw at this time.",
  },
  inconclusive: {
    label: "Couldn't tell", at: "", network: false,
    where: "We couldn't tell where this one broke",
    meaning: "The measurements we had did not point at one part of the path, so we are not guessing.",
    next: "Nothing to do for a single event. If the same device keeps freezing, copy the report below and send it to Weavian support.",
  },
};

export const placeOf = (segment) => PLACES[segment] || PLACES.inconclusive;

export const KINDS = {
  replies_stopped: "The device kept sending, but nothing came back.",
  sending_stopped: "The device stopped sending on this connection.",
  both_stopped: "Both directions went quiet at the same moment.",
};

/* One reading of a record, from either a history row or a full episode's
 * verdict. `tone` also names the timeline marker, so a dot and the card it
 * opens always agree. */
export function reading({ segment, confidence, recovered, closing_state: closing, needs_reassessment: old } = {}) {
  if (old) {
    return {
      ...PLACES.inconclusive, label: "Not known", tone: "legacy", sure: "Old record", where: "Location withheld",
      meaning: "This record was made with an older method that could point at the wrong place. Its measurements are kept. Its conclusion is not.",
      next: "Nothing to do. Newer records use a stricter method.",
    };
  }
  if (recovered) {
    return {
      ...PLACES.inconclusive, label: "Brief pause", tone: "resumed", sure: "Recovered on its own", where: "A pause that ended by itself",
      meaning: "The connection went quiet for a few seconds and then carried on. Pauses like this are common, and once traffic is moving again there is nothing left to measure.",
      next: "Nothing to do, unless the same device keeps showing pauses.",
    };
  }
  if (!segment || segment === "inconclusive" || !PLACES[segment]) {
    return {
      ...PLACES.inconclusive, tone: "unknown", sure: "Couldn't tell",
      ...(closing && { meaning: "The connection was being closed at that moment, which is how a call or session normally ends." }),
    };
  }
  const sure = confidence === "confirmed";
  return { ...PLACES[segment], tone: sure ? "located" : "suspected", sure: sure ? "We're sure" : "Likely, not proven" };
}

// A connection that stopped for a few seconds and moved again on its own. The
// console keeps these apart from interruptions and shows them only on request.
export const isPause = (record) => reading(record).tone === "resumed";

/* Each tone as the timeline's legend names it, in the order a marker holding
 * several records picks the one it shows and opens: a known place first, a pause
 * that ended by itself last. */
export const TONES = [
  { tone: "located", legend: "We know where it broke" },
  { tone: "suspected", legend: "We have a likely place" },
  { tone: "unknown", legend: "Couldn't tell" },
  { tone: "legacy", legend: "Old record" },
  { tone: "resumed", legend: "Brief pause, recovered on its own" },
];

const article = (word) => /^[aeiou]/i.test(word) ? "An" : "A";

// "A Google Meet connection from front-desk froze".
export function headline({ className, device, recovered }) {
  const what = className ? `${article(className)} ${className} connection` : "A connection";
  return `${what} from ${device || "an unknown device"} ${recovered ? "paused for a moment" : "froze"}`;
}
